export interface SessionState {
  layout: LayoutNode;
  panels: Record<string, Panel>;
  focusedPanelId: string;
  /** Per-panel pixel sizes keyed by panelId. Persisted via LocalSessionAdapter. */
  panelSizes?: Record<string, number>;
}

export type LayoutNode =
  | { type: 'panel'; panelId: string }
  | { type: 'split'; direction: 'h' | 'v'; children: LayoutNode[] };

export type TabType = 'document' | 'collection' | 'view' | 'recording' | 'canvas' | 'space' | 'conversation';

export interface TabEntry {
  /**
   * For type 'conversation': a stable conversationId UUID minted at tab creation
   * (§7.4), e.g. '550e8400-e29b-41d4-a716-446655440000'. The id is the real
   * conversationId from the first render — no provisional placeholder. Conversation
   * tabs are exempt from pruning by type, so a tab with a freshly-minted UUID
   * (no DB row yet) survives the SessionPruner bootstrap pass.
   * For all other types: the entity id or named view slug (e.g. 'documents', 'home').
   */
  id: string;
  type: TabType;
  /**
   * Which surface of this entity was open, so reopening the tab lands on the
   * page the user left rather than the first one. Optional: absent in every
   * session persisted before this existed, and on tab types that have no
   * surfaces. The surface may also be deleted while the tab remembers it, so
   * consumers must validate against the live manifest before using it.
   */
  surfaceId?: string;
}

export interface Panel {
  panelId: string;
  activeId: string;
  tabs: TabEntry[];
}

export interface DraggableTabData {
  type: 'tab';
  tabId: string;
  fromPanelId: string;
  fromTabIndex: number;
}

export interface DroppableTabBarData {
  type: 'tabbar';
  panelId: string;
}

export const NAMED_VIEWS = new Set(['home', 'documents', 'collections', 'trash'] as const);
export type NamedView = 'home' | 'documents' | 'collections' | 'trash';
